import { gql, useMutation } from '@apollo/client';
import { Alert } from '@apollo/orbit';
import React, { useMemo, useState } from 'react';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { LintDiagnosticLevel } from 'src/lib/graphqlTypes/types';

import { ConfirmationModal } from '../../operationCheckResults/operationCheckDetails/overrideButton/confirmationModal/ConfirmationModal';

import { DefaultOverrideViolationButton } from './defaultOverrideViolationButton/DefaultOverrideViolationButton';
import { UndoOverrideViolationButton } from './undoOverrideViolationButton/UndoOverrideViolationButton';

export const OverrideViolationButtonIgnoredRuleFragment = gql`
  fragment OverrideViolationButtonIgnoredRuleFragment on IgnoredRule {
    ignoredRule
    schemaCoordinate
  }
`;
export const OverrideViolationButtonLintDiagnosticFragment = gql`
  fragment OverrideViolationButtonLintDiagnosticFragment on LintDiagnostic {
    rule
    coordinate
    level
  }
`;

interface Props {
  violation: GraphQLTypes.OverrideViolationButtonLintDiagnosticFragment;
  subgraphName?: string | null;
  ignoredLinterViolations: GraphQLTypes.OverrideViolationButtonIgnoredRuleFragment[];
  defaultOverrideButtonClassName: string;
  undoButtonClassName: string;
}
export const OverrideViolationButton = ({
  violation,
  subgraphName,
  ignoredLinterViolations,
  defaultOverrideButtonClassName,
  undoButtonClassName,
}: Props) => {
  const { currentLintCheckDetailsRoute, currentCustomCheckDetailsRoute } =
    useInternalGraphLinking();
  const { graphId } = useRouteParams(
    currentLintCheckDetailsRoute,
    currentCustomCheckDetailsRoute,
  );
  const [updateIgnoredRuleViolations] = useMutation<
    GraphQLTypes.UpdateIgnoredRuleViolationsMutation,
    GraphQLTypes.UpdateIgnoredRuleViolationsMutationVariables
  >(
    gql`
      mutation UpdateIgnoredRuleViolationsMutation(
        $graphId: ID!
        $changes: LinterIgnoredRuleChangesInput!
      ) {
        service(id: $graphId) {
          id
          updateIgnoredRuleViolations(changes: $changes) {
            ignoredRule
            schemaCoordinate
            subgraphName
          }
        }
      }
    `,
    {
      update(cache, response, { variables }) {
        const successfulResult =
          response.data?.service?.updateIgnoredRuleViolations;
        if (!successfulResult) return;
        cache.modify({
          id: cache.identify({ __typename: 'Service', id: variables?.graphId }),
          fields: { ignoredLinterViolations: () => successfulResult },
        });
      },
    },
  );

  const confirmAction = useMemo(() => {
    const rulePreviouslyMarkedAsIgnored = ignoredLinterViolations.some(
      (ignoredViolation) =>
        ignoredViolation.ignoredRule === violation.rule &&
        ignoredViolation.schemaCoordinate === violation.coordinate,
    );
    if (violation.level === LintDiagnosticLevel.IGNORED) {
      return rulePreviouslyMarkedAsIgnored ? 'unignore' : 'undoUnignore';
    } else {
      return rulePreviouslyMarkedAsIgnored ? 'undoIgnore' : 'ignore';
    }
  }, [ignoredLinterViolations, violation]);
  const [isIgnoreModalOpen, setIgnoreModalOpen] = useState<boolean>(false);
  const [isUnignoreModalOpen, setUnignoreModalOpen] = useState<boolean>(false);

  return (
    <>
      {confirmAction === 'ignore' ? (
        <DefaultOverrideViolationButton
          graphId={graphId}
          buttonText="Ignore"
          onClick={() => setIgnoreModalOpen(true)}
          defaultOverrideButtonClassName={defaultOverrideButtonClassName}
        />
      ) : confirmAction === 'unignore' ? (
        <DefaultOverrideViolationButton
          graphId={graphId}
          buttonText="Unignore"
          onClick={() => setUnignoreModalOpen(true)}
          defaultOverrideButtonClassName={defaultOverrideButtonClassName}
        />
      ) : confirmAction === 'undoIgnore' ? (
        /* Shows "Marked as ignored on next run" button.
           Clicking "Undo on this button should open up the UnignoreModalOpen for confirmation. */
        <UndoOverrideViolationButton
          graphId={graphId}
          action="ignored"
          onUndoClick={() => setUnignoreModalOpen(true)}
          undoButtonClassName={undoButtonClassName}
        />
      ) : confirmAction === 'undoUnignore' ? (
        /* Shows "Marked as unignore on next run" button.
           Clicking "Undo on this button should open up the IgnoreModalOpen for confirmation. */
        <UndoOverrideViolationButton
          graphId={graphId}
          action="unignored"
          onUndoClick={() => setIgnoreModalOpen(true)}
          undoButtonClassName={undoButtonClassName}
        />
      ) : (
        assertUnreachable(confirmAction)
      )}

      <ConfirmationModal
        isOpen={isIgnoreModalOpen}
        title="Ignore this linter rule violation"
        description={
          <>
            By selecting this action, you acknowledge that you are ignoring this
            rule violation. If ignored, this violation will not cause future
            linter runs to fail. <RerunBanner />
          </>
        }
        onSubmit={() => {
          updateIgnoredRuleViolations({
            variables: {
              graphId,
              changes: {
                ruleViolationsToIgnore: [
                  {
                    ignoredRule: violation.rule,
                    schemaCoordinate: violation.coordinate,
                    subgraphName,
                  },
                ],
                ruleViolationsToEnable: [],
              },
            },
          });
        }}
        onClose={() => {
          setIgnoreModalOpen(false);
        }}
      />
      <ConfirmationModal
        isOpen={isUnignoreModalOpen}
        title="Unignore this linter rule violation"
        description={
          <>
            By selecting this action, you acknowledge that you are unignoring
            this rule violation. This violation will now cause future linter
            runs to fail. <RerunBanner />
          </>
        }
        onSubmit={() => {
          updateIgnoredRuleViolations({
            variables: {
              graphId,
              changes: {
                ruleViolationsToIgnore: [],
                ruleViolationsToEnable: [
                  {
                    ignoredRule: violation.rule,
                    schemaCoordinate: violation.coordinate,
                    subgraphName,
                  },
                ],
              },
            },
          });
        }}
        onClose={() => {
          setUnignoreModalOpen(false);
        }}
      />
    </>
  );
};

const RerunBanner = () => (
  <Alert status="info">
    <span className="font-semibold">To apply the changes,</span>{' '}
    <span>you must rerun or create a new check.</span>
  </Alert>
);
